/**
 * Lit until you look at it, then gone in eight seconds.
 *
 * A row that changed stays marked however long you leave it — a dig runs for
 * minutes and you come back an hour later. It stops being marked when it is
 * genuinely ON YOUR SCREEN, not when the tab is opened: a report can be forty
 * rows long and the three that changed can be at the bottom of it.
 *
 * Once seen it fades rather than snapping off, so the eye can still find what
 * it was reading. `jc-seen` runs the fade; the class is dropped when it ends.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * WHICH MARKED ROWS ARE INSERTIONS. Read through context, not props.
 *
 * `unseen` is data about one table and is passed to it; this is a room-wide
 * display hint — an item id is unique, so "was it an insertion" has one answer
 * wherever it is drawn. Threading it beside `unseen` would have meant the same
 * optional set declared in fifteen components for a difference in colour.
 *
 * Empty by default, which is exactly the old behaviour: everything marked gets
 * the one hue.
 */
const FreshIds = createContext<ReadonlySet<number>>(new Set());
export const FreshProvider = FreshIds.Provider;
export const useFreshIds = () => useContext(FreshIds);

/** How long a row stays visibly faded after being seen. Matches the CSS. */
export const FADE_MS = 8_000;

/** A row must be on screen this long to count as read — a scroll flying past
 *  it is not looking at it. */
const DWELL_MS = 600;

export type SeenMarks = {
  /** Ref for the row element; give it the row's id. */
  mark: (id: number) => (el: HTMLElement | null) => void;
  /** Ref for an element that stands for SEVERAL items — a landscape row whose
   *  players sit behind a collapsed cell. Seeing it reads all of them.
   *
   *  `key` NAMES THE ELEMENT, and a report with more than one grid over the
   *  same items must pass it: the landscape draws a section per axis, so the
   *  same player is a member of a row in each of them. Without a key the
   *  registry was keyed by the group's FIRST id, two sections' rows collided
   *  on it, and the later one to mount unobserved the earlier one's element —
   *  a row you were looking at, silently no longer watched, so its badge could
   *  not be cleared by any amount of looking or reloading. */
  markGroup: (ids: readonly number[], key?: string) => (el: HTMLElement | null) => void;
  /** '', 'jc-unseen' or 'jc-seen' for a row. */
  cls: (id: number) => string;
  /** The same, for an element standing for several items: lit while any of
   *  them is waiting, fading while any is. */
  clsAny: (ids: readonly number[]) => string;
  /** Scroll the first thing still waiting into view. With a hundred cards on
   *  screen, "eleven are new" is not findable by scrolling and hoping. False
   *  when nothing drawn here stands for anything waiting. */
  next: () => boolean;
  /** Read everything waiting HERE without visiting it — the report you are
   *  looking at, not the room, because the button that calls this says "new
   *  HERE". IT LEAVES NOTHING LIT: no amber, and no fade either. */
  readAll: () => void;
  /** How many are still waiting AND reachable here. */
  waitingHere: number;
  /** Rows still lit here: waiting, or read and not yet faded. The "new here"
   *  pill stays while this is above zero, so it does not vanish from under a
   *  row that is still glowing. */
  glowing: number;
};

/**
 * @param unseen ids that have changed and not been looked at
 * @param onSeen called with ids that have now been on screen; the caller drops
 *        them from the badge, which is the same set
 */
export function useSeen(
  unseen: ReadonlySet<number> | undefined,
  onSeen: (ids: number[]) => void,
  /** Every row this table has, scrolled to or not. Anything waiting that is
   *  NOT one of them can never be read here, so it is dropped on sight. */
  known?: readonly number[],
): SeenMarks {
  // KEYED BY ELEMENT, NOT BY ITEM. One rendered thing — a row, a card — is one
  // key, and it stands for one or more ids. Keying these by an id instead made
  // two elements that share an item fight over the same slot.
  const els = useRef(new Map<string, HTMLElement>());
  const timers = useRef(new Map<string, number>());
  /** key -> the ids that element stands for. Refreshed on every render, so a
   *  row that gains a player is read with the player in it. */
  const members = useRef(new Map<string, readonly number[]>());
  const [fading, setFading] = useState<Map<number, number>>(new Map());
  const fresh = useFreshIds();
  const seenCb = useRef(onSeen);
  seenCb.current = onSeen;
  // WHAT WAS ACTUALLY WAITING TO BE READ, readable from inside the observer
  // callback, which is created once and would otherwise close over the first
  // render's set forever.
  const waiting = useRef(unseen);
  waiting.current = unseen;

  // One observer for the whole table; rows register and deregister themselves.
  const observer = useRef<IntersectionObserver | null>(null);
  if (observer.current === null && typeof IntersectionObserver !== 'undefined') {
    observer.current = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          // An element may stand for SEVERAL items — a landscape row is a
          // whole cell of players, and the chips inside it are behind a
          // collapsed cell nobody has opened. Seeing the row is seeing them.
          const key = String((e.target as HTMLElement).dataset.seenId ?? '');
          const ids = members.current.get(key) ?? [];
          if (!ids.length) continue;
          if (e.isIntersecting) {
            // A ROW NOBODY WAS WAITING ON IS NOT NEWS. Every row is observed —
            // the hook cannot know in advance which will be marked — but only
            // one that was actually unseen has anything to fade FROM. Without
            // this, scrolling lit up the whole table: each row reaching the
            // viewport was added to the fading set, and the fade starts at full
            // amber. Scroll, everything glows; scroll again, glows again.
            const due = ids.filter(i => waiting.current?.has(i));
            if (!due.length) continue;
            if (timers.current.has(key)) continue;
            timers.current.set(
              key,
              window.setTimeout(() => {
                timers.current.delete(key);
                // Still unseen when the dwell finished? Another table may have
                // read them in the meantime.
                const now = ids.filter(i => waiting.current?.has(i));
                if (!now.length) return;
                const at = Date.now();
                setFading(f => {
                  const next = new Map(f);
                  for (const i of now) next.set(i, at);
                  return next;
                });
                seenCb.current(now);
              }, DWELL_MS),
            );
          } else {
            const t = timers.current.get(key);
            if (t) {
              window.clearTimeout(t);
              timers.current.delete(key);
            }
          }
        }
      },
      // A CARD CAN BE TALLER THAN THE WINDOW. At 0.5 an opportunity card that
      // fills the screen never reaches half-visible, so it never counted as
      // read — thirteen on the tab, two cleared, eleven that would not move
      // however far you scrolled. Any of it on screen, for the dwell, is
      // looking at it.
      { threshold: 0.01 },
    );
  }

  // A BADGE MUST BE CLEARABLE. Anything waiting that this table has no row for
  // can never come on screen here, so it would sit on the tab forever — which
  // is exactly what stale ids from an earlier mapping did, stuck at twenty-odd
  // and unmoved by opening the tab. Dropped as soon as the table renders.
  //
  // The caveat: `known` is the rows AFTER the reader's own filters, so marks on
  // rows a filter is hiding are cleared too. A badge that cannot go down is the
  // worse failure.
  const knownKey = known ? known.join(',') : null;
  useEffect(() => {
    // An EMPTY table is a table that has not loaded, not a table with no room
    // for anything — pruning against it would silently clear every badge while
    // the report was still fetching.
    if (!known?.length || !unseen?.size) return;
    const here = new Set(known);
    const gone = Array.from(unseen).filter(id => !here.has(id));
    if (gone.length) seenCb.current(gone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownKey, unseen]);

  useEffect(() => {
    const obs = observer.current;
    const running = timers.current;
    return () => {
      obs?.disconnect();
      running.forEach(t => window.clearTimeout(t));
      running.clear();
    };
  }, []);

  // Drop a row from the fading set once its animation is over, so the class
  // stops being applied and the row is plainly normal again.
  //
  // WOKEN AT THE MOMENT THE OLDEST ONE EXPIRES, not on a poll. A one-second
  // re-check only rescheduled itself when the set CHANGED, and it returns the
  // same set while nothing has expired — so it stopped after one tick and the
  // fade class stuck until some other render came along.
  useEffect(() => {
    if (!fading.size) return;
    const oldest = Math.min(...Array.from(fading.values()));
    const due = Math.max(0, oldest + FADE_MS - Date.now());
    const t = window.setTimeout(() => {
      const cutoff = Date.now() - FADE_MS;
      setFading(f => {
        const next = new Map(Array.from(f).filter(([, at]) => at > cutoff));
        return next.size === f.size ? f : next;
      });
    }, due);
    return () => window.clearTimeout(t);
  }, [fading]);

  // ONE STABLE REF CALLBACK PER ROW, cached forever.
  //
  // Returning a fresh closure each render makes React detach and reattach
  // every row's ref on every render — unobserve, then observe. `observe()`
  // delivers its first notification asynchronously, and the next unobserve
  // cancels it; a room re-renders on every websocket event, so during a dig
  // that notification was cancelled over and over and the dwell timer never
  // started. Rows stayed lit no matter how long you looked at them.
  const refs = useRef(new Map<string, (el: HTMLElement | null) => void>());
  /** One cached ref callback per KEY, and the ids it stands for kept fresh. */
  const register = useCallback((key: string, ids: readonly number[]) => {
    members.current.set(key, ids);
    const had = refs.current.get(key);
    if (had) return had;
    const fn = (el: HTMLElement | null) => {
      const prev = els.current.get(key);
      if (prev && prev !== el) observer.current?.unobserve(prev);
      if (el) {
        el.dataset.seenId = key;
        els.current.set(key, el);
        observer.current?.observe(el);
      } else {
        els.current.delete(key);
      }
    };
    refs.current.set(key, fn);
    return fn;
  }, []);

  const mark = useCallback((id: number) => register(`item-${id}`, [id]), [register]);

  const markGroup = useCallback(
    (ids: readonly number[], key?: string) => register(key ?? `group-${ids.join(',')}`, ids),
    [register],
  );

  // A ROW THAT CHANGED IS NOT A ROW THAT ARRIVED, so it wears a different hue.
  // The modifier rides along into the fade, or a sky-blue row would fade out
  // through amber.
  const hue = useCallback(
    (id: number) => (fresh.has(id) ? '' : ' jc-hue-update'),
    [fresh],
  );
  const cls = useCallback(
    (id: number) =>
      unseen?.has(id)
        ? `jc-unseen${hue(id)}`
        : fading.has(id)
          ? `jc-seen${hue(id)}`
          : '',
    [unseen, fading, hue],
  );

  const clsAny = useCallback(
    (ids: readonly number[]) => {
      // A GROUP IS AN INSERTION IF ANY OF IT IS. One new player behind a
      // collapsed cell is news; the rest having merely changed is not a reason
      // to under-report it.
      const anyFresh = ids.some(i => fresh.has(i));
      const mark = anyFresh ? '' : ' jc-hue-update';
      if (ids.some(i => unseen?.has(i))) return `jc-unseen${mark}`;
      return ids.some(i => fading.has(i)) ? `jc-seen${mark}` : '';
    },
    [unseen, fading, fresh],
  );

  const next = useCallback(() => {
    // In the order the table renders them, not the order the room found them.
    for (const [key, el] of Array.from(els.current.entries())) {
      const ids = members.current.get(key) ?? [];
      if (!ids.some(i => waiting.current?.has(i))) continue;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    }
    return false;
  }, []);

  // COUNTED ONCE. `known` is what the report can show, and a report may show
  // the same item in more than one grid — the landscape draws a section per
  // axis over one set of players — so the raw list holds it once per section.
  // Counting that list said "40 new here" where forty players were five copies
  // of eight.
  const here = known ? Array.from(new Set(known)).filter(id => unseen?.has(id)) : [];
  // Readable from inside a callback made once, the same reason `waiting` is.
  const hereNow = useRef(here);
  hereNow.current = here;

  // NOT "the badge was clicked". Twelve rows arriving in a table of forty is
  // worth walking; twelve hundred after a long dig is not, and there was no
  // way to say so — the only way down was to scroll past every one of them.
  //
  // AND IT LEAVES THE TABLE PLAIN. A row read ON SCREEN fades rather than
  // snapping off, because the eye is still on it and a row that vanishes
  // mid-glance cannot be found again. Nobody is looking at the twelve hundred:
  // clearing them into a half-minute fade left the whole report amber, which is
  // the thing the button was pressed to stop. So the fade is dropped too — the
  // rows already mid-fade from being read on screen included, since a fading
  // row is still a lit row.
  const readAll = useCallback(() => {
    const ids = hereNow.current;
    setFading(f => (f.size ? new Map() : f));
    if (ids.length) seenCb.current(ids);
  }, []);

  return { mark, markGroup, cls, clsAny, next, readAll, waitingHere: here.length, glowing: here.length + fading.size };
}

/**
 * ONE TRACKER FOR A WHOLE REPORT.
 *
 * A report is rarely one table: the feature matrix draws a grid per company,
 * the price map a table per solution type, the landscape a section per axis,
 * and every one of them has the open-questions list hanging underneath. Handed
 * the room's set directly, each built a tracker of its own — and a tracker
 * drops anything waiting that it has no row for, so the first to render pruned
 * the marks belonging to all the rest. The questions list, whose rows are the
 * three or four questions still open, pruned nearly everything.
 *
 * That is a badge saying four, opening onto nothing lit and no button to
 * press: the marks were thrown away between the tab and the row.
 *
 * So the report builds one of these from every id it can bring on screen —
 * across all of its tables, after its own filters — and hands it down. Then
 * `waitingHere` is the count for the whole report, and one button walks it.
 *
 * @param ids every row the report can show; nulls (a question with no item
 *        behind it) are dropped rather than read as an id
 */
export function useReportSeen(
  unseen: ReadonlySet<number> | undefined,
  onSeen: ((ids: number[]) => void) | undefined,
  ids: readonly (number | null | undefined)[],
): SeenMarks {
  const known = ids.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useSeen(unseen, onSeen ?? NOOP, known);
}

const NOOP = () => {};
